import React, { createContext, useContext, useState, ReactNode } from 'react';
import { noop } from 'lodash';
import { ENTITY_TYPE, PARENT_ENTITY_TYPE, STANDALONE_AUTO_COMPLETE_DELAY } from '../../constants';
import { ReportEligibilityResponseItem, useReportingEligibility } from '../../hooks';

export interface ReportingContextData {
    entityType: ENTITY_TYPE;
    parentEntityType?: PARENT_ENTITY_TYPE;
    entityId: string;
    parentEntityId?: string;
    sellerId?: string;
    buyerId?: string;
    isModalOpen: boolean;
    eligibilityList: ReportEligibilityResponseItem[];
    isStandalone?: boolean;
    openModal: (entityId?: string) => void;
    closeModal: () => void;
    disableReporting: (id: string) => void;
    autoCompleteDelay?: number;
    onSuccess?: () => void;
    onComplete?: (reportId: string) => void;
    onError?: () => void;
}

const DEFAULT_CONTEXT: ReportingContextData = {
    entityType: ENTITY_TYPE.DEFAULT,
    entityId: '',
    isModalOpen: false,
    eligibilityList: [],
    isStandalone: false,
    openModal: noop,
    closeModal: noop,
    disableReporting: noop,
    onSuccess: noop,
};

const ReportingContext = createContext<ReportingContextData>(DEFAULT_CONTEXT);

interface ReportingContextProviderProps
    extends Pick<
        ReportingContextData,
        | 'entityType'
        | 'parentEntityType'
        | 'parentEntityId'
        | 'buyerId'
        | 'sellerId'
        | 'onSuccess'
        | 'onComplete'
        | 'onError'
        | 'isStandalone'
    > {
    entityId: string | string[];
    children: ReactNode;
}

export const ReportingContextProvider = ({
    children,
    entityType,
    parentEntityType,
    entityId,
    parentEntityId,
    sellerId,
    buyerId,
    isStandalone,
    onSuccess,
    onComplete,
    onError,
}: ReportingContextProviderProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentEntityId, setCurrentEntityId] = useState(Array.isArray(entityId) ? entityId[0] : entityId);
    const entityIds = Array.isArray(entityId) ? entityId : [entityId];

    const openModal = (entityIdOverride?: string) => {
        if (entityIdOverride) {
            setCurrentEntityId(entityIdOverride);
        }
        setIsModalOpen(true);
    };
    const closeModal = () => setIsModalOpen(false);
    const { eligibilityList, disableReporting } = useReportingEligibility(entityType, entityIds);

    return (
        <ReportingContext.Provider
            value={{
                entityType,
                parentEntityType,
                entityId: currentEntityId,
                parentEntityId,
                sellerId,
                buyerId,
                isModalOpen,
                eligibilityList,
                isStandalone,
                openModal,
                closeModal,
                disableReporting,
                onSuccess,
                onComplete,
                onError,
                autoCompleteDelay: STANDALONE_AUTO_COMPLETE_DELAY,
            }}
        >
            {children}
        </ReportingContext.Provider>
    );
};

export const useReportingContext = () => useContext(ReportingContext);
